import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState(false);
    const [estados, setEstados] = useState([]);

    const loadCatalogos = useCallback(async () => {
        const resp = await getData('catalogos/estados-personal', usuario.token);
        if (resp.error === '') setEstados(resp.data || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    const loadData = useCallback(async () => {
        if (globalsContext.idEdit) {
            loadingModal.setOpen(true);
            try {
                const response = await getData(`personal/${globalsContext.idEdit}`, usuario.token);
                if (response.error === '') {
                    const p = response.data;
                    setData({
                        id_personal: p.id_personal,
                        legajo: p.legajo,
                        id_estado_personal: p.id_estado_personal,
                        fecha_ingreso: p.fecha_ingreso,
                        observaciones: p.observaciones,
                        apellido: p.persona?.apellido,
                        nombre: p.persona?.nombre,
                        tipo_documento: p.persona?.tipo_documento,
                        dni: p.persona?.dni,
                        cuil: p.persona?.cuil,
                        sexo: p.persona?.sexo,
                        email_personal: p.persona?.email_personal,
                        telefono: p.persona?.telefono,
                        domicilio: p.persona?.domicilio,
                    });
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
            } finally {
                loadingModal.setOpen(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => { loadCatalogos(); }, [loadCatalogos]);
    useEffect(() => { if (globalsContext.idEdit) loadData(); }, [globalsContext.idEdit, loadData]);

    const onClose = useCallback(() => navigate(-1), [navigate]);
    const onCancel = () => alertModal.showModal('Cancelar', 'Se perderán los cambios. ¿Continuar?', onClose);

    const onSubmit = async (values) => {
        loadingModal.setOpen(true);
        try {
            const payload = {
                apellido: values.apellido,
                nombre: values.nombre,
                tipo_documento: values.tipo_documento || 'DNI',
                dni: values.dni || null,
                cuil: values.cuil || null,
                sexo: values.sexo || null,
                email_personal: values.email_personal || null,
                telefono: values.telefono || null,
                domicilio: values.domicilio || null,
                legajo: values.legajo || null,
                id_estado_personal: Number(values.id_estado_personal),
                observaciones: values.observaciones || null,
            };
            let response;
            if (data && data.id_personal) {
                response = await putData(`personal/${data.id_personal}`, payload, usuario.token);
            } else {
                response = await postData('personal', payload, usuario.token);
            }
            if (response.error === '') {
                enqueueSnackbar(data ? 'Personal modificado' : 'Personal creado', { variant: 'success' });
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const formSections = useMemo(() => [
        {
            name: 'Datos personales',
            fields: [
                {
                    dataName: 'apellido', label: 'Apellido', controlType: 'textField',
                    initialValue: data ? data.apellido : '',
                    validationSchema: yup.string().required('El apellido es requerido'),
                    autoFocus: true,
                },
                {
                    dataName: 'nombre', label: 'Nombre', controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido'),
                },
                {
                    dataName: 'tipo_documento', label: 'Tipo de documento', controlType: 'select',
                    items: [{ id: 'DNI', nombre: 'DNI' }, { id: 'LE', nombre: 'LE' }, { id: 'LC', nombre: 'LC' }, { id: 'PAS', nombre: 'Pasaporte' }],
                    initialValue: data ? (data.tipo_documento || 'DNI') : 'DNI',
                    validationSchema: yup.string().nullable(),
                },
                {
                    dataName: 'dni', label: 'Número de documento', controlType: 'textField',
                    initialValue: data ? (data.dni || '') : '',
                    validationSchema: yup.string().nullable().max(20),
                },
                {
                    dataName: 'cuil', label: 'CUIL', controlType: 'textField',
                    initialValue: data ? (data.cuil || '') : '',
                    validationSchema: yup.string().nullable().max(20),
                },
                {
                    dataName: 'sexo', label: 'Sexo', controlType: 'select',
                    items: [{ id: 'F', nombre: 'Femenino' }, { id: 'M', nombre: 'Masculino' }, { id: 'X', nombre: 'Otro' }],
                    initialValue: data ? (data.sexo || '') : '',
                    validationSchema: yup.string().nullable(),
                },
                {
                    dataName: 'email_personal', label: 'Email personal', controlType: 'textField',
                    initialValue: data ? (data.email_personal || '') : '',
                    validationSchema: yup.string().email('Email inválido').nullable(),
                },
                {
                    dataName: 'telefono', label: 'Teléfono', controlType: 'textField',
                    initialValue: data ? (data.telefono || '') : '',
                    validationSchema: yup.string().nullable().max(50),
                },
                {
                    dataName: 'domicilio', label: 'Domicilio', controlType: 'textField',
                    initialValue: data ? (data.domicilio || '') : '',
                    validationSchema: yup.string().nullable().max(255),
                },
            ],
        },
        {
            name: 'Legajo y estado',
            fields: [
                {
                    dataName: 'legajo', label: 'Legajo', controlType: 'textField',
                    initialValue: data ? (data.legajo || '') : '',
                    validationSchema: yup.string().nullable().max(50),
                },
                {
                    dataName: 'id_estado_personal', label: 'Estado del personal', controlType: 'select',
                    items: estados,
                    initialValue: data ? String(data.id_estado_personal || '') : '',
                    validationSchema: yup.number().required('El estado es requerido'),
                },
                {
                    dataName: 'observaciones', label: 'Observaciones', controlType: 'textField',
                    initialValue: data ? (data.observaciones || '') : '',
                    validationSchema: yup.string().nullable().max(500),
                },
            ],
        },
    ], [data, estados]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? 'Modificar Personal' : 'Nuevo Personal'}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <Paper className={classes.paper}>
                            <CustomForm
                                formSections={formSections}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear personal'}
                                cancelButtonText="Cancelar"
                                stepperType={'vertical'}
                                isModal={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
